import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AirlineSeatReclineExtraRoundedIcon from '@mui/icons-material/AirlineSeatReclineExtraRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import heroCamp from './images/ti7/hero-camp.jpg';
import detailFront from './images/ti7/detail-front.jpg';
import detailSide from './images/ti7/detail-side.jpg';
import detailRear from './images/ti7/detail-rear.jpg';
import sevenSeaterWide from './images/ti7/seven-seater-wide.jpg';
import powerOf7 from './images/ti7/front.jpeg';
import dmpPoster from './images/ti7/rear-dmp.jpeg';
import debutPoster from './images/ti7/debut-tenaga-monster.jpg';
import interiorDash from './images/ti7/interior-dash.jpg';
import interiorRow2 from './images/ti7/interior-row2.jpg';
import interiorRoof from './images/ti7/interior-roof.jpg';
import interiorBrown from './images/ti7/interior-brown.jpg';
import interiorBlack from './images/ti7/interior-black.jpg';
import interiorWhite from './images/ti7/interior-white.jpg';
import v2lCamp from './images/ti7/v2l-camp.jpg';
import carJade from './images/ti7/car-jadegreen.png';
import carMountain from './images/ti7/car-mountaingreen.png';
import carObsidian from './images/ti7/car-obsidianblack.png';
import carSilver from './images/ti7/car-starlightsilver.png';
import carTitanium from './images/ti7/car-titaniumgrey.png';
import posterDark from './images/launch/prebook-poster-dark.jpg';
import PageSeo, { breadcrumbJsonLd, keywordSets } from './PageSeo';
import CarColorShowcase from './CarColorShowcase';
import PreBookSpotlight from './PreBookSpotlight';
import ExpandingCards from './ExpandingCards';
import { createWhatsAppUrl } from './siteConfig';

// Status per akhir Oktober 2026: pre-book resmi dibuka di BYD Arista BSD City.
// Harga OTR dan spesifikasi final Indonesia belum dirilis BYD Motor Indonesia.
// Foto, nama warna, dan angka teknis mengikuti halaman resmi BYD UK
// (byd.com/uk, varian Excellence AWD); kelengkapan Indonesia bisa berbeda.
const UPDATED_AT = '31 Oktober 2026';

const WA_BOOKING = createWhatsAppUrl(
  'Halo Junius, saya ingin pre-book BYD Ti7 di BYD Arista BSD City. Mohon info ketentuan tanda jadi, pilihan warna, dan estimasi pengiriman.',
);
const WA_UPDATE = createWhatsAppUrl(
  'Halo Junius, saya ingin didaftarkan untuk update BYD Ti7 (harga resmi, spesifikasi Indonesia, dan jadwal pengiriman).',
);

const timeline = [
  {
    date: '31 Juli 2026',
    title: 'Debut: SUV 7-seater pertama BYD',
    desc: 'BYD memperkenalkan Ti7 DM-p sebagai SUV tujuh kursi pertamanya: DM-p Super Hybrid AWD 408 PS dengan jarak listrik murni 119 km.',
  },
  {
    date: 'Oktober 2026',
    title: 'Dikonfirmasi untuk Indonesia',
    desc: 'Jaringan dealer BYD Indonesia membuka open indent Ti7 sebagai SUV plug-in hybrid DM-p AWD. Spesifikasi lokal dikonfirmasi saat peluncuran.',
  },
  {
    date: '30 Oktober 2026',
    title: 'Open Pre-Book di BYD Arista BSD City',
    desc: 'Pre-book Ti7 dibuka bersama BYD Atto 2. Pemesan awal mendapat prioritas unit dan penawaran eksklusif lewat Junius.',
  },
  {
    date: 'Menunggu pengumuman',
    title: 'Harga resmi dan jadwal pengiriman',
    desc: 'BYD Motor Indonesia belum merilis harga OTR dan spesifikasi final. Pemesan pre-book dikabari lebih dulu begitu harga keluar.',
  },
];

// Nama warna dan swatch dari halaman BYD UK. Render mobil dipotong dari foto
// studio resmi (latar dihapus). Warna untuk Indonesia menunggu pengumuman.
const exteriorColors = [
  { label: 'Jade Green', start: '#1fa39f', end: '#035655', image: carJade },
  { label: 'Starlight Silver', start: '#eceef2', end: '#b4b6bd', image: carSilver },
  { label: 'Mountain Green', start: '#9aa5a5', end: '#4f5a5b', image: carMountain },
  { label: 'Titanium Grey', start: '#7b8794', end: '#333c47', image: carTitanium },
  { label: 'Obsidian Black', start: '#5b636a', end: '#15191d', image: carObsidian },
];

const showcaseStats = [
  { label: 'Tenaga sistem', value: 408, unit: 'PS' },
  { label: '0-100 km/jam', value: 4.8, unit: 'detik', decimals: 1 },
  { label: 'Jarak listrik murni WLTP', value: 119, unit: 'km' },
];

const heroStats = [
  { icon: AirlineSeatReclineExtraRoundedIcon, value: '7 kursi', label: 'SUV 7-seater pertama BYD' },
  { icon: BoltRoundedIcon, value: '408 PS', label: 'DM-p Super Hybrid AWD' },
  { icon: SpeedRoundedIcon, value: '4,8 detik', label: '0-100 km/jam' },
  { icon: RouteRoundedIcon, value: '119 km', label: 'Jarak listrik murni WLTP' },
];

const exteriorGallery = [
  {
    src: detailSide,
    title: 'Siluet tiga bagian',
    desc: 'Struktur geometris yang bersih mendefinisikan BYD Ti7: siluet tiga bagian yang jelas untuk keseimbangan visual dan kehadiran yang kuat. Bodi tegak sepanjang 5.146 mm dengan garis horizontal menciptakan bentuk kokoh yang memaksimalkan ruang sekaligus proporsi.',
  },
  {
    src: detailFront,
    title: 'Lampu depan LED khas',
    desc: 'Lampu depan LED signature dengan garis cahaya horizontal yang menyatu ke grille memberi wajah tegas dan mudah dikenali, lengkap dengan animasi sambutan saat mobil dibuka. Fender melebar dan velg 20 inci menegaskan karakter off-road.',
  },
  {
    src: detailRear,
    title: 'Lampu belakang LED lebar',
    desc: 'Bagian belakang menampilkan lampu LED khas dengan elemen horizontal yang menegaskan lebar dan struktur, menghadirkan kesan kokoh dan percaya diri di jalan. Ban serep di pintu belakang berengsel samping melengkapi gaya petualangnya.',
  },
];

const campaignPosters = [
  { src: powerOf7, ratio: 125, title: 'The Power of 7', desc: 'Tujuh kursi, tujuh alasan untuk keluarga Indonesia.' },
  { src: dmpPoster, ratio: 125, title: 'DM-P Plug-in Hybrid', desc: 'AWD, jarak listrik kota hingga 164 km, DC 30-80% dalam 17,5 menit.' },
  { src: debutPoster, ratio: 99.7, title: 'Debut dengan tenaga monster', desc: '408 PS dari mesin 1.5 turbo dan dua motor listrik.' },
];

const interiorGallery = [
  { src: interiorDash, ratio: 56.3, title: 'Kokpit pintar dan terhubung', desc: 'Layar sentuh 15,6 inci, panel instrumen 10,25 inci, dual wireless charging 50 W, dan ekosistem DiLink.' },
  { src: interiorRow2, ratio: 56.3, title: 'Baris kedua lega', desc: 'Kursi baris kedua bisa diatur 4 arah, berpenghangat dan berventilasi di sisi luar.' },
  { src: interiorRoof, ratio: 56.3, title: 'Atap kaca panoramik', desc: 'Kabin terasa lapang, kaca akustik di depan, privacy glass dari pintu belakang.' },
];

const interiorColors = [
  { src: interiorBrown, label: 'Cokelat', ratio: 51.1 },
  { src: interiorBlack, label: 'Hitam', ratio: 66.4 },
  { src: interiorWhite, label: 'Abu terang', ratio: 62.5 },
];

const dmpFeatures = [
  { title: 'DM-p Super Hybrid', desc: 'Mesin bensin 1.5 turbo 150 PS yang terutama bekerja sebagai pembangkit listrik, dipadu motor listrik depan dan belakang untuk AWD cerdas.' },
  { title: 'Blade Battery 35,6 kWh', desc: 'Jarak listrik murni hingga 119 km WLTP, atau hingga 164 km untuk pemakaian kota. Harian bisa sepenuhnya listrik, perjalanan jauh tetap bebas cemas.' },
  { title: 'Pengisian cepat', desc: 'DC fast charging 30-80% sekitar 17,5 menit. V2L mengubah Ti7 menjadi sumber listrik untuk berkemah atau kebutuhan darurat.' },
  { title: 'Performa SUV besar', desc: '408 PS, 0-100 km/jam 4,8 detik, kecepatan maksimum sekitar 190 km/jam, kapasitas tarik 2.500 kg.' },
];

const specGroups = [
  {
    title: 'Dimensi & Kabin',
    rows: [
      ['Panjang x Lebar x Tinggi', '5.146 x 1.995 x 1.865 mm'],
      ['Konfigurasi kursi', '7 kursi, 3 baris'],
      ['Bagasi 7 kursi / 5 kursi / 2 kursi', '126 / 970 / 1.830 L'],
      ['Velg', '20 inci alloy'],
      ['Berat kosong', '2.510 kg'],
      ['Kapasitas tarik', '2.500 kg'],
    ],
  },
  {
    title: 'Powertrain & Performa (Excellence AWD)',
    rows: [
      ['Sistem', 'DM-p Super Hybrid plug-in, AWD'],
      ['Mesin bensin', '1.5 L turbo, 150 PS'],
      ['Tenaga sistem', '408 PS (sekitar 300 kW)'],
      ['Baterai', 'Blade Battery 35,6 kWh'],
      ['Jarak listrik murni', '119 km WLTP (hingga 164 km kota)'],
      ['Akselerasi 0-100 km/jam', '4,8 detik'],
      ['Kecepatan maksimum', 'Sekitar 190 km/jam'],
      ['Pengisian DC', '30-80% sekitar 17,5 menit'],
      ['V2L', 'Ada'],
    ],
  },
  {
    title: 'Kenyamanan & Teknologi',
    rows: [
      ['Layar', 'Head unit 15,6 inci, instrumen digital 10,25 inci'],
      ['Audio', '18 speaker'],
      ['Kursi', 'Kulit; depan dan baris 2 luar berpenghangat dan berventilasi'],
      ['AC', '3 zona'],
      ['Konsol tengah', 'Kulkas dan penghangat minuman, 2 wireless charger 50 W'],
      ['Atap', 'Panoramic glass roof'],
      ['Kaca', 'Akustik di depan, privacy glass belakang'],
      ['Warna eksterior', 'Starlight Silver, Jade Green, Mountain Green, Titanium Grey, Obsidian Black'],
      ['Warna interior', 'Hitam, abu terang, cokelat'],
    ],
  },
];

const confirmed = [
  'Open pre-book resmi dibuka 30 Oktober 2026 di BYD Arista BSD City bersama BYD Atto 2.',
  'SUV plug-in hybrid DM-p dengan penggerak AWD, model BYD terbesar dan paling serbaguna sejauh ini.',
  'SUV tujuh kursi pertama BYD dengan tenaga sistem 408 PS dan jarak listrik murni 119 km WLTP.',
];

const pending = [
  'Harga OTR Jakarta dan pilihan varian untuk Indonesia.',
  'Kelengkapan fitur per varian.',
  'Jadwal pengiriman unit pre-book.',
];

const faqs = [
  {
    q: 'Apa itu BYD Ti7?',
    a: 'Ti7 adalah SUV besar bergaya off-road dengan sistem DM-p Super Hybrid plug-in dan AWD. Hadir dengan tujuh kursi, tenaga 408 PS, dan jarak listrik murni 119 km WLTP.',
  },
  {
    q: 'Apakah BYD Ti7 sudah bisa dipesan di BYD BSD Serpong?',
    a: 'Sudah. Pre-book resmi dibuka 30 Oktober 2026 di BYD Arista BSD City. Hubungi Junius untuk ketentuan tanda jadi, pilihan warna, dan estimasi pengiriman.',
  },
  {
    q: 'Berapa harga BYD Ti7 di Indonesia?',
    a: 'Harga resmi belum diumumkan BYD Motor Indonesia. Pemesan pre-book akan dikabari lebih dulu begitu harga OTR keluar.',
  },
  {
    q: 'Apa bedanya Ti7 dengan BYD M6 DM?',
    a: 'M6 DM adalah MPV plug-in hybrid untuk keluarga. Ti7 adalah SUV bergaya off-road dengan AWD, tenaga jauh lebih besar, ground clearance tinggi, dan ban serep di pintu belakang.',
  },
];

function JelajahiTi7() {
  const isMobile = useMediaQuery('(max-width:768px)');
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <PageSeo
        title='BYD Ti7 Open Pre-Book | SUV 7-Seater DM-p AWD di BYD BSD Serpong'
        description='Pre-book BYD Ti7 sudah dibuka di BYD Arista BSD City. SUV 7-seater pertama BYD dengan DM-p Super Hybrid AWD 408 PS dan jarak listrik murni 119 km. Lihat foto, warna, interior, referensi spesifikasi, dan hubungi Junius untuk pre-book.'
        keywords={[
          ...keywordSets.ti7,
          ...keywordSets.localDealer,
          'BYD Ti7 Super Hybrid',
          'BYD Ti7 Jade Green',
          'SUV off-road hybrid BYD',
        ]}
        path='/jelajahi-ti7'
        image={heroCamp}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'BYD Ti7', path: '/jelajahi-ti7' },
          ]),
          faqJsonLd,
        ]}
      />

      <Box component='main' sx={{ width: '100%', overflow: 'hidden' }}>
        <HeroSection isMobile={isMobile} />
        <SevenSeaterSection />
        <TimelineSection />
        <CarColorShowcase
          title='BYD Ti7'
          kicker='Exterior'
          description='Lima pilihan warna BYD Ti7: Jade Green, Starlight Silver, Mountain Green, Titanium Grey, dan Obsidian Black. Konfirmasi ketersediaan warna saat pre-book bersama Junius.'
          colors={exteriorColors}
          defaultColor='Jade Green'
          imageRatio={50.4}
          stats={showcaseStats}
          cta={{ label: 'Pre-book', href: WA_BOOKING, external: true }}
        />
        <ExteriorSection />
        <InteriorSection />
        <DmpSection />
        <PreBookSpotlight variant='compact' exclude='ti7' />
        <SpecSection />
        <StatusSection />
        <FaqSection />
        <CtaSection />
      </Box>
    </>
  );
}

function HeroSection({ isMobile }) {
  return (
    <>
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '62vh', md: '100vh' },
          display: 'flex',
          alignItems: 'flex-end',
          overflow: 'hidden',
          backgroundColor: '#0b1a1f',
          color: '#fff',
        }}
      >
        <Box
          component='img'
          src={isMobile ? detailFront : heroCamp}
          alt='BYD Ti7 Jade Green saat berkemah malam hari, SUV 7-seater DM-p'
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: { xs: 'center 40%', md: '62% center' },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: {
              xs: 'linear-gradient(180deg, rgba(5,12,20,0.5) 0%, rgba(5,12,20,0.05) 35%, rgba(5,12,20,0.82) 100%)',
              md: 'linear-gradient(90deg, rgba(5,12,20,0.8) 0%, rgba(5,12,20,0.45) 40%, rgba(5,12,20,0.05) 70%), linear-gradient(180deg, rgba(5,12,20,0.3) 0%, rgba(5,12,20,0) 30%, rgba(5,12,20,0.6) 100%)',
            },
          }}
        />
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            width: '100%',
            maxWidth: 1240,
            mx: 'auto',
            px: { xs: 2, md: 5 },
            pb: { xs: 4, md: 7 },
          }}
        >
          <Typography
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.6,
              borderRadius: '999px',
              backgroundColor: 'rgba(212,175,55,0.18)',
              border: '1px solid rgba(212,175,55,0.65)',
              color: '#f1d87a',
              fontSize: '0.78rem',
              fontWeight: 800,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            <BoltRoundedIcon sx={{ fontSize: 16 }} /> Open Pre-Book · DM-p Super Hybrid AWD
          </Typography>
          <Typography
            component='h1'
            sx={{
              mt: 1.5,
              fontSize: { xs: '2.6rem', sm: '3.6rem', md: '5rem' },
              fontWeight: 900,
              lineHeight: 0.98,
            }}
          >
            BYD Ti7
          </Typography>
          <Typography
            sx={{ mt: 1, fontSize: { xs: '1.05rem', md: '1.3rem' }, fontWeight: 700, color: '#f1d87a' }}
          >
            The Power of 7 is here. Luxury in every journey.
          </Typography>
          {!isMobile && <HeroDetails />}
        </Box>
      </Box>

      {isMobile && (
        <Box sx={{ backgroundColor: '#0b1a1f', color: '#fff', px: 2, pt: 3, pb: 4 }}>
          <HeroDetails />
        </Box>
      )}
    </>
  );
}

function HeroDetails() {
  return (
    <>
      <Typography
        sx={{
          mt: { xs: 0, md: 1.5 },
          maxWidth: 640,
          fontSize: { xs: '1rem', md: '1.1rem' },
          lineHeight: 1.7,
          color: 'rgba(255,255,255,0.88)',
        }}
      >
        SUV tujuh kursi pertama BYD dengan gaya off-road, DM-p Super Hybrid
        plug-in, dan penggerak AWD. Pre-book sudah dibuka di BYD Arista BSD
        City. Amankan unit lebih awal dan dapatkan penawaran eksklusif untuk
        pemesan pertama.
      </Typography>

      <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          href={WA_BOOKING}
          target='_blank'
          rel='noopener noreferrer'
          variant='contained'
          startIcon={<WhatsAppIcon />}
          sx={{
            px: 3,
            py: 1.3,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 800,
            backgroundColor: '#d4af37',
            color: '#1a1400',
            '&:hover': { backgroundColor: '#e3c35a' },
          }}
        >
          Pre-book sekarang via WhatsApp
        </Button>
        <Button
          href='#spesifikasi-ti7'
          variant='outlined'
          sx={{
            px: 3,
            py: 1.3,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 800,
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.55)',
            '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
          }}
        >
          Lihat spesifikasi
        </Button>
      </Box>

      <Box
        sx={{
          mt: 3.5,
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: 1,
          maxWidth: 820,
        }}
      >
        {heroStats.map(({ icon: Icon, value, label }) => (
          <Box
            key={label}
            sx={{
              px: 1.5,
              py: 1.2,
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.22)',
              backgroundColor: 'rgba(5,12,20,0.5)',
              backdropFilter: 'blur(6px)',
            }}
          >
            <Icon sx={{ fontSize: 20, color: '#f1d87a' }} />
            <Typography sx={{ mt: 0.4, fontSize: { xs: '1.1rem', md: '1.35rem' }, fontWeight: 900 }}>
              {value}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.78)' }}>{label}</Typography>
          </Box>
        ))}
      </Box>
      <Typography sx={{ mt: 1.2, fontSize: '0.74rem', color: 'rgba(255,255,255,0.6)' }}>
        Spesifikasi varian Excellence AWD. Kelengkapan untuk Indonesia
        dikonfirmasi BYD Motor Indonesia saat peluncuran.
      </Typography>
    </>
  );
}

function SevenSeaterSection() {
  return (
    <Box sx={{ backgroundColor: '#0b1a1f' }}>
      <Box sx={{ position: 'relative', maxWidth: 1600, mx: 'auto' }}>
        <Box
          component='img'
          src={sevenSeaterWide}
          alt="BYD's first seven-seater is coming to Indonesia: BYD Ti7 tampak samping di depan gedung klasik"
          width={2000}
          height={1250}
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        />
        <Typography
          sx={{
            position: 'absolute',
            right: { xs: 10, md: 20 },
            bottom: { xs: 8, md: 14 },
            px: 1.2,
            py: 0.4,
            borderRadius: '6px',
            backgroundColor: 'rgba(0,0,0,0.55)',
            color: 'rgba(255,255,255,0.85)',
            fontSize: '0.72rem',
          }}
        >
          SUV 7-seater pertama BYD · open pre-book di BYD Arista BSD City
        </Typography>
      </Box>
    </Box>
  );
}

function TimelineSection() {
  return (
    <Box sx={{ backgroundColor: '#f4f6f8', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Status terkini · diperbarui {UPDATED_AT}</Eyebrow>
        <SectionTitle>Perjalanan Ti7 menuju BSD</SectionTitle>
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
            gap: 2,
          }}
        >
          {timeline.map((item, index) => {
            const isPending = index === timeline.length - 1;
            return (
              <Box
                key={item.title}
                sx={{
                  p: 2.5,
                  borderRadius: '14px',
                  backgroundColor: '#fff',
                  border: isPending ? '1px dashed rgba(212,175,55,0.9)' : '1px solid rgba(17,24,39,0.08)',
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    color: isPending ? '#8a6d1d' : '#fff',
                    backgroundColor: isPending ? 'rgba(212,175,55,0.18)' : '#0f7d7a',
                  }}
                >
                  {isPending ? <EventAvailableRoundedIcon sx={{ fontSize: 20 }} /> : <VerifiedRoundedIcon sx={{ fontSize: 20 }} />}
                </Box>
                <Typography
                  sx={{ mt: 1.5, fontSize: '0.78rem', fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: '#0f7d7a' }}
                >
                  {item.date}
                </Typography>
                <Typography sx={{ mt: 0.5, fontWeight: 900, color: '#111827', lineHeight: 1.25 }}>{item.title}</Typography>
                <Typography sx={{ mt: 0.8, color: '#4b5563', lineHeight: 1.6, fontSize: '0.95rem' }}>{item.desc}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function Figure({ item }) {
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '16px',
        overflow: 'hidden',
        backgroundColor: '#e5e7eb',
        '&::before': { content: '""', display: 'block', paddingTop: `${item.ratio}%` },
        '&:hover img': { transform: 'scale(1.03)' },
      }}
    >
      <Box
        component='img'
        src={item.src}
        alt={`BYD Ti7: ${item.title}`}
        loading='lazy'
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          transition: 'transform 0.5s ease',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          p: { xs: 2, md: 2.5 },
          pt: 6,
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.74) 100%)',
          color: '#fff',
        }}
      >
        <Typography sx={{ fontWeight: 900, fontSize: { xs: '1rem', md: '1.15rem' } }}>{item.title}</Typography>
        <Typography sx={{ mt: 0.4, fontSize: '0.9rem', lineHeight: 1.5, color: 'rgba(255,255,255,0.86)' }}>{item.desc}</Typography>
      </Box>
    </Box>
  );
}

function ExteriorSection() {
  return (
    <Box sx={{ backgroundColor: '#fff', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Eksterior</Eyebrow>
        <SectionTitle>Urban box family SUV</SectionTitle>
        <Typography sx={{ mt: 1.5, maxWidth: 760, color: '#4b5563', lineHeight: 1.7 }}>
          Proporsi kotak bergaya off-road dengan kaca depan tegak, fender
          melebar, dan pintu belakang berengsel samping yang membawa ban serep.
        </Typography>
        <Box sx={{ mt: 4 }}>
          <ExpandingCards items={exteriorGallery} modelName='BYD Ti7' height={520} />
        </Box>

        <Typography sx={{ mt: 5, fontWeight: 900, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#111827' }}>
          Materi kampanye
        </Typography>
        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {campaignPosters.map((item) => (
            <Figure key={item.title} item={item} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function InteriorSection() {
  return (
    <Box sx={{ backgroundColor: '#f4f6f8', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Interior</Eyebrow>
        <SectionTitle>Designed for comfort, powered by technology</SectionTitle>
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {interiorGallery.map((item) => (
            <Figure key={item.title} item={item} />
          ))}
        </Box>

        <Typography sx={{ mt: 5, fontWeight: 900, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#111827' }}>
          Tiga pilihan warna kabin
        </Typography>
        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          {interiorColors.map((item) => (
            <Box
              key={item.label}
              sx={{
                borderRadius: '14px',
                overflow: 'hidden',
                backgroundColor: '#fff',
                border: '1px solid rgba(17,24,39,0.08)',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  '&::before': { content: '""', display: 'block', paddingTop: '56%' },
                }}
              >
                <Box
                  component='img'
                  src={item.src}
                  alt={`Interior BYD Ti7 warna ${item.label}`}
                  loading='lazy'
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>
              <Typography sx={{ p: 1.5, fontWeight: 800, color: '#111827' }}>{item.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function DmpSection() {
  return (
    <Box sx={{ backgroundColor: '#0b1a1f', color: '#fff', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
          gap: { xs: 3, md: 5 },
          alignItems: 'center',
        }}
      >
        <Box
          component='img'
          src={v2lCamp}
          alt='BYD Ti7 menyalakan perlengkapan berkemah lewat V2L di malam hari'
          loading='lazy'
          sx={{ width: '100%', height: 'auto', borderRadius: '16px', display: 'block', boxShadow: '0 30px 60px rgba(0,0,0,0.45)' }}
        />
        <Box>
          <Eyebrow light>DM-p Super Hybrid</Eyebrow>
          <SectionTitle light>Listrik untuk harian, hybrid untuk jauh</SectionTitle>
          <Box sx={{ mt: 3, display: 'grid', gap: 1.5 }}>
            {dmpFeatures.map((item) => (
              <Box
                key={item.title}
                sx={{ p: 2, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.12)', backgroundColor: 'rgba(255,255,255,0.05)' }}
              >
                <Typography sx={{ fontWeight: 900 }}>{item.title}</Typography>
                <Typography sx={{ mt: 0.5, lineHeight: 1.6, fontSize: '0.95rem', color: 'rgba(255,255,255,0.78)' }}>{item.desc}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SpecSection() {
  return (
    <Box id='spesifikasi-ti7' sx={{ backgroundColor: '#f4f6f8', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 }, scrollMarginTop: 96 }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Referensi spesifikasi</Eyebrow>
        <SectionTitle>BYD Ti7 DM-p Excellence AWD</SectionTitle>
        <Typography sx={{ mt: 1.5, maxWidth: 800, color: '#4b5563', lineHeight: 1.7 }}>
          Spesifikasi resmi BYD untuk varian Excellence AWD. Versi Indonesia
          dikonfirmasi sebagai DM-p AWD, spesifikasi final diumumkan BYD Motor
          Indonesia saat peluncuran.
        </Typography>
        <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
          {specGroups.map((group) => (
            <Box
              key={group.title}
              component='table'
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid rgba(17,24,39,0.08)',
                alignSelf: 'start',
                '& caption': { textAlign: 'left', px: 2, py: 1.5, fontWeight: 900, color: '#fff', backgroundColor: '#0b1a1f' },
                '& td': { px: 2, py: 1.2, borderTop: '1px solid rgba(17,24,39,0.08)', fontSize: '0.95rem', verticalAlign: 'top' },
                '& td:first-of-type': { color: '#4b5563', width: '48%' },
                '& td:last-of-type': { fontWeight: 700, color: '#111827' },
              }}
            >
              <caption>{group.title}</caption>
              <tbody>
                {group.rows.map(([label, value]) => (
                  <tr key={label}>
                    <td>{label}</td>
                    <td>{value}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function StatusSection() {
  return (
    <Box sx={{ backgroundColor: '#fff', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 3, md: 4 } }}>
        <Box sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: '16px', backgroundColor: '#eaf6f5', border: '1px solid rgba(15,125,122,0.35)' }}>
          <Eyebrow>Sudah dipastikan</Eyebrow>
          <SectionTitle small>Yang sudah kami ketahui</SectionTitle>
          <Box component='ul' sx={{ mt: 2, pl: 2.5, m: 0, color: '#1f2937', lineHeight: 1.7 }}>
            {confirmed.map((item) => (
              <Box component='li' key={item} sx={{ mb: 1 }}>{item}</Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: '16px', backgroundColor: '#f8f9fb', border: '1px solid rgba(17,24,39,0.1)' }}>
          <Eyebrow>Menunggu pengumuman resmi</Eyebrow>
          <SectionTitle small>Yang masih kami tunggu</SectionTitle>
          <Box component='ul' sx={{ mt: 2, pl: 2.5, m: 0, color: '#1f2937', lineHeight: 1.7 }}>
            {pending.map((item) => (
              <Box component='li' key={item} sx={{ mb: 1 }}>{item}</Box>
            ))}
          </Box>
          <Typography sx={{ mt: 2, fontSize: '0.82rem', color: '#6b7280' }}>
            Sumber: spesifikasi dan materi resmi BYD serta materi pre-book BYD
            Arista BSD City.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function FaqSection() {
  return (
    <Box sx={{ backgroundColor: '#f4f6f8', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        <Eyebrow>FAQ</Eyebrow>
        <SectionTitle>Pertanyaan yang sering masuk</SectionTitle>
        <Box sx={{ mt: 3, display: 'grid', gap: 1.5 }}>
          {faqs.map((item) => (
            <Box key={item.q} sx={{ p: 2.5, borderRadius: '12px', backgroundColor: '#fff', border: '1px solid rgba(17,24,39,0.08)' }}>
              <Typography component='h3' sx={{ fontWeight: 900, color: '#111827', fontSize: '1.05rem' }}>{item.q}</Typography>
              <Typography sx={{ mt: 0.8, color: '#4b5563', lineHeight: 1.7 }}>{item.a}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CtaSection() {
  return (
    <Box sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 }, backgroundColor: '#07111f', color: '#fff' }}>
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '0.8fr 1.2fr' },
          gap: { xs: 3, md: 5 },
          alignItems: 'center',
        }}
      >
        <Box sx={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.14)', boxShadow: '0 30px 60px rgba(0,0,0,0.45)' }}>
          <Box
            component='img'
            src={posterDark}
            alt='Poster open pre-book BYD Ti7 dan BYD Atto 2, hubungi Junius 0812 8686 4825'
            loading='lazy'
            width={1023}
            height={1352}
            sx={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </Box>
        <Box>
          <Eyebrow light>BYD Arista BSD City</Eyebrow>
          <Typography component='h2' sx={{ mt: 1, fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 900, lineHeight: 1.05 }}>
            Pre-book BYD Ti7 sekarang
          </Typography>
          <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
            Hubungi Junius untuk mengamankan unit Ti7 Anda. Pemesan pre-book
            mendapat prioritas unit dan penawaran eksklusif, lalu dikabari lebih
            dulu begitu BYD mengumumkan harga resmi dan jadwal pengiriman.
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              href={WA_BOOKING}
              target='_blank'
              rel='noopener noreferrer'
              variant='contained'
              startIcon={<WhatsAppIcon />}
              sx={{ px: 4, py: 1.3, borderRadius: '8px', textTransform: 'none', fontWeight: 800, backgroundColor: '#25D366', '&:hover': { backgroundColor: '#1EBE54' } }}
            >
              Pre-book BYD Ti7 via WhatsApp
            </Button>
            <Button
              href={WA_UPDATE}
              target='_blank'
              rel='noopener noreferrer'
              variant='outlined'
              startIcon={<NotificationsActiveRoundedIcon />}
              sx={{ px: 3, py: 1.3, borderRadius: '8px', textTransform: 'none', fontWeight: 800, color: '#fff', borderColor: 'rgba(255,255,255,0.45)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' } }}
            >
              Daftar update harga resmi
            </Button>
            <Button
              component={RouterLink}
              to='/jelajahi-atto2'
              variant='text'
              sx={{ px: 2, py: 1.3, textTransform: 'none', fontWeight: 800, color: '#f1d87a' }}
            >
              Lihat juga BYD Atto 2
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function Eyebrow({ children, light = false }) {
  return (
    <Typography
      sx={{ color: light ? '#f1d87a' : '#0f7d7a', fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', fontSize: '0.82rem' }}
    >
      {children}
    </Typography>
  );
}

function SectionTitle({ children, small = false, light = false }) {
  return (
    <Typography
      component='h2'
      sx={{
        mt: 1,
        fontSize: small ? { xs: '1.5rem', md: '1.9rem' } : { xs: '2rem', md: '3rem' },
        fontWeight: 900,
        color: light ? '#fff' : '#111827',
        lineHeight: 1.05,
      }}
    >
      {children}
    </Typography>
  );
}

export default JelajahiTi7;
